import Headroom from "headroom.js";
import DetailsAnimation from '@w0s/details-animation';
import HeaderSearch from './_searchOverlay';
import SearchResults from './_searchResults';
import MegaMenu from "./_megamenu";
import MobileMenu from "./_mobileMenu";
import NewsFilters from "./_newsFilters";
import newsStickyCarousel from "./_newsStickyCarousel";
import NewsSingle from "./_newsSingle";

export const General = () => {

    // Sticky header
    const headroomSticky = () => {
        const headroom = new Headroom(document.querySelector('.site-header'), {
            offset: 220,
            tolerance: 0,
        });

        // Initialize Headroom on all pages except category-reports
        if (
            !document.body.classList.contains('category-reports') ||
            !document.body.classList.contains('category-informes')
        ) {
            headroom.init();
        }
    }

    // Language selector
    const languageSelector = () => {
        const languageSelector = document.querySelector('.lang-selector');
        if (!languageSelector) return;

        const langDropdown = languageSelector.querySelector('.lang-dropdown');

        // Open menu on click
        languageSelector.addEventListener('click', () => {
            console.log('click');
            languageSelector.classList.add('open');
        });

        // Close when leaving the dropdown
        langDropdown.addEventListener('mouseleave', () => {
            languageSelector.classList.remove('open');
        });

        // Close on mobile
        document.addEventListener('click', (e) => {
            if (!languageSelector.contains(e.target)) {
                languageSelector.classList.remove('open');
            }
        });

    }

    // Language modal
    const languageModal = () => {
        const langModal = document.querySelector('.geo-modal');
        if (!langModal) return;

        const langModalState = localStorage.getItem('langModal');
        const langModalClose = langModal.querySelector('.geo-modal__close');
        const langBtns = langModal.querySelectorAll('.wp-block-button__link');

        // Check if modal has been closed before
        if (!langModalState) {
            langModal.classList.remove('d-none');
        }

        // Set local storage when modal is closed
        langModalClose.addEventListener('click', () => {
            localStorage.setItem('langModal', 'closed');
            langModal.remove();
        });

        // Set local storage when button is clicked
        langBtns.forEach((btn) => {
            btn.addEventListener('click', () => {
                localStorage.setItem('langModal', 'closed');
                langModal.remove();
            });
        });

    }

    // Social sharing
	const socialSharing = () => {
        const copyURL = document.querySelector('.social-share__copy');
        if (!copyURL) return;
    
        copyURL.addEventListener('click', (e) => {
            e.preventDefault();
            navigator.clipboard.writeText(window.location.href)
                .then(() => {
                    // Success: URL copied to clipboard
                    toastAlert('URL copied to clipboard', 'success');
                })
                .catch((err) => {
                    // Clipboard API failed
                    console.error('Failed to copy URL:', err);
                    toastAlert('Failed to copy URL to clipboard', 'error');
                });
        });
    }    

    // Toast alert
	const toastAlert = (message, type) => {
		const toast = document.createElement('div');
		toast.classList.add('toast', 'show', type);
		toast.innerHTML = message;
		document.body.appendChild(toast);

		setTimeout(() => {
			toast.remove();
		}, 4000);
	}

    // Details animation
    const detailsAnimation = () => {
        const details = document.querySelectorAll('.wp-block-details');
        if (!details) return;

        for (const targetElement of details) {
            new DetailsAnimation(targetElement);
        }

        // if any details element is open, close all others
        details.forEach((detailsElement) => {
            detailsElement.addEventListener('toggle', (e) => {
                if (e.target.open) {
                    details.forEach((otherDetailsElement) => {
                        if (otherDetailsElement !== e.target) {
                            otherDetailsElement.open = false;
                        }
                    });
                }
            });
        });
    }

    // Animate swiper bullets
    const swiperBullets = () => {
        const sliders = document.querySelectorAll('.swiper');
    
        sliders.forEach((slider) => {
            const bulletsWrapper = slider.querySelector('.swiper-pagination');
            
            // Observe dynamically added elements
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.classList && node.classList.contains('swiper-pagination-bullet')) {
                            const bullets = slider.querySelectorAll('.swiper-pagination-bullet');

                            // Add animation classes to each bullet
                            bullets.forEach((bullet) => {
                                bullet.classList.add('animate', 'fade-in-bottom');
                                observeVisibility(bullet); // Observe for visibility
                            });
    
                            // Check if there's 1 or fewer bullets and remove bulletsWrapper if needed
                            if (bullets.length <= 1) {
                                bulletsWrapper.remove(); // Remove bulletsWrapper
                                observer.disconnect(); // Stop observing since no more bullets are needed
                            }
                        }
                    });
                });
            });
    
            observer.observe(slider, { childList: true, subtree: true });
    
            // Intersection Observer to detect when bullets come into view
            const observeVisibility = (bullet) => {
                const intersectionObserver = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('viewed'); // Add "viewed" when in the viewport
                            intersectionObserver.unobserve(entry.target); // Stop observing once viewed
                        }
                    });
                });
    
                intersectionObserver.observe(bullet);
            };
        });
    };
    

    // Modify buttons with outline style
    const modifyButtons = () => {
        const btnOutlined = document.querySelectorAll('.is-style-outline .wp-block-button__link');
        if (!btnOutlined) return;

        btnOutlined.forEach((button) => {
            // Wrap button text in span
            const text = button.textContent;
            button.innerHTML = `<span>${text}</span>`;
        });
    }

    // Responsive table
    const responsiveTable = () => {
        const tables = document.querySelectorAll('.wp-block-table');
    
        if (!tables) return;
    
        tables.forEach((table) => {
            // Get all table headers from the current table's thead
            const headers = table.querySelectorAll('thead th');
    
            // Loop through all rows in the table body
            const rows = table.querySelectorAll('tbody tr');
            rows.forEach((row) => {
                // Get all cells (td) within the current row
                const cells = row.querySelectorAll('td');
    
                // Loop through each cell and assign the corresponding header text as the data-label
                cells.forEach((cell, index) => {
                    // Check if there's a corresponding header for this cell
                    if (headers[index]) {
                        const headerText = headers[index].textContent.trim();
                        cell.setAttribute('data-label', headerText);
                    }
                });
            });
        });
    }

    // Details accordion
    const detailsAccordion = () => {
        const accordions = document.querySelectorAll('.wp-block-details');
        if (!accordions) return;
    
        accordions.forEach((accordion) => {
            const summary = accordion.querySelector('summary');
            if (!summary) return;
    
            // Get the inner HTML of the summary
            let summaryContent = summary.innerHTML;
    
            // Split the content by the first <br> tag
            let contentParts = summaryContent.split(/<br\s*\/?>/);
    
            // Rebuild the content: keep the first part as it is, wrap the subsequent parts in a <p> tag
            summary.innerHTML = `${contentParts[0]}<p>${contentParts.slice(1).join(' ')}</p>`;
        });
    };  

    // Footer mobile menu
    const footerMobileMenu = () => {
        const footerWidgets = document.querySelectorAll('.footer-widgets');
        if (!footerWidgets) return;

        footerWidgets.forEach((item) => {

            // Find any .widget_nav_menu and add class to parent
            const navMenu = item.querySelectorAll('.widget_nav_menu');
            navMenu.forEach((menu) => {
                menu.parentElement.classList.add('has-nav-menu');
            });
        });

        const hasMenu = document.querySelectorAll('.has-nav-menu');
        if (!hasMenu) return;

        hasMenu.forEach((item) => {
            const menuTitle = item.querySelector('.wp-block-heading');

            // add a <i class="material-icons">chevron_right</i> to the menu title
            const chevron = document.createElement('i');
            chevron.classList.add('material-icons');
            chevron.textContent = 'chevron_right';
            menuTitle.appendChild(chevron);

            // Add a click event to the menu title
            menuTitle.addEventListener('click', () => {
                item.classList.toggle('open');
                
                // Change the icon based on the open class
                const icon = menuTitle.querySelector('i');
                if (item.classList.contains('open')) {
                    icon.textContent = 'expand_less';
                } else {
                    icon.textContent = 'chevron_right';
                }

            });
        });
    }

    // Initialize

    headroomSticky();
    languageSelector();
    languageModal();
    socialSharing();
    detailsAnimation();
    swiperBullets();
    modifyButtons();
    responsiveTable();
    detailsAccordion();
    footerMobileMenu();

    HeaderSearch();
    SearchResults();
    MobileMenu();
    MegaMenu();
    NewsFilters();
    newsStickyCarousel();
    NewsSingle();
};